import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ShowsService } from './services/shows.service';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { SnackbarTypeEnum } from 'src/app/data/models/enum/snackbar-type.enum';
import { CustomSnackbarService } from 'src/app/ui/components/custom-snackbar/custom-snackbar-service';
import { GenericDialogComponent } from 'src/app/ui/components/generic-dialog/generic-dialog.component';
import { SnackBarComponent } from 'src/app/ui/components/snack-bar/snack-bar.component';
import { Show } from './models/show.interface';
import { CreateShowDto, UpdateShowDto, ShowParamsDto } from './dtos';
import { ShowFormComponent } from './components/show-form/show-form.component';
import { GenericDialogDto } from 'src/app/data/models/dto/generic-dialog.dto';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { genericDialog } from 'src/app/data/utils/generic-dialog.utils';
import { ClassRoomsService } from 'src/app/data/services/classroom.service';
import { ClassroomFilterDto } from 'src/app/data/models/dto/classroom-filter.dto';
import { YearSelectComponent } from 'src/app/ui/components/year-select/year-select.component';
import { BookingsReportComponent } from './components/bookings-report/bookings-report.component';

@Component({
  selector: 'app-shows',
  templateUrl: './shows.component.html',
  styleUrls: ['./shows.component.scss']
})
export class ShowsComponent implements OnInit, OnDestroy {
  @ViewChild('yearSelectId') yearSelect!: YearSelectComponent
  isUpdate: boolean = false;
  showId: string | undefined;
  show: Show | undefined;

  private dialogConfig: MatDialogConfig = {
    disableClose: true,
    autoFocus: true,
  };

  private subscribeDialog = new Subscription();
  private serviceSubscription = new Subscription();

  shows: Show[] = []
  startTxtYear = ''
  params: ShowParamsDto = {active: true}
  idSchool: string = this.school[0].id
  filterClassroom: ClassroomFilterDto = {
    year: this.startTxtYear,
    idSchool: this.idSchool,
  };

  years?: string[]
  selectedYear: string = ''
  selectedIdsClassrooms: string[] = []
  selectedIdsProfiles: string[] = []
  profilesFilter: string[] = []
  typeShow: string[] = []
  titleText: string = ''
  loading: boolean = true
  disableResetFilters: boolean = true
  showStatusFilter?: boolean = undefined
  showReleasedFilter?: boolean = undefined
  showMonthFilter?: string = this.getActualMonth()

  constructor(
    private readonly showsService: ShowsService,
    private readonly classRoomsService: ClassRoomsService,
    private readonly dialog: MatDialog,
    private readonly snackBar: SnackBarComponent,
    private readonly snackbarService: CustomSnackbarService,
    private readonly userUtil: UserUtil
  ) { }

  private get school(){
    return this.userUtil.getSchools()
  }

  ngOnDestroy(): void {
    this.serviceSubscription.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    await this.getYears()
  }

  //#region service data

  getActualMonth(){
    return new Date().getMonth().toString()
  }

  async getYears(): Promise<void> {
    const result = await this.classRoomsService
      .getYears(this.filterClassroom)
      .toPromise();
    if (result && result.length > 0) {
      this.years = result;
      this.selectedYear = result[0];
    }
  }

  searchText(text:string){
    this.titleText = text
    this.resetFiltersStatus()
    this.reloadShows()
  }

  changeYear(year: string) {
    this.selectedYear = year;
    this.selectedIdsClassrooms = [];
    this.resetFiltersStatus()
    this.reloadShows()
  }

  async handleSelectClassroom(ids: string[]) {
    this.selectedIdsClassrooms = ids;
    this.resetFiltersStatus()
    this.reloadShows()
  }

  async handleSelectProfiles(ids: string[]) {
    this.profilesFilter = ids;
    this.resetFiltersStatus()
    this.reloadShows()
  }

  async handleSelectShowStatus(value: boolean | undefined) {
    this.showStatusFilter = value;
    this.resetFiltersStatus()
    this.reloadShows()
  }

  async handleSelectShowMonth(value: string | undefined) {
    this.showMonthFilter = value;
    this.resetFiltersStatus()
    this.reloadShows()
  }

  async handleSelectShowReleased(value: boolean | undefined) {
    this.showReleasedFilter = value;
    this.resetFiltersStatus()
    this.reloadShows()
  }

  resetFilters(){
    if (
      this.selectedYear != this.years![0] ||
      this.selectedIdsClassrooms.length > 0 ||
      this.selectedIdsProfiles.length > 0 ||
      this.showReleasedFilter != undefined ||
      this.showMonthFilter != this.getActualMonth() ||
      this.showStatusFilter != undefined
    ) {
      this.selectedYear = this.years![0];
      this.selectedIdsClassrooms = []
      this.profilesFilter = []
      this.showReleasedFilter = undefined
      this.showMonthFilter = this.getActualMonth()
      this.showStatusFilter = undefined
      this.yearSelect.reset()
    }
    this.disableResetFilters = true
    this.reloadShows()
  }

  resetFiltersStatus() {
    this.disableResetFilters =
      this.titleText == '' &&
      this.selectedYear == this.years![0] &&
      this.selectedIdsClassrooms.length == 0 &&
      this.selectedIdsProfiles.length == 0 &&
      this.showReleasedFilter == undefined &&
      this.showMonthFilter == this.getActualMonth() &&
      this.showStatusFilter == undefined
  }

  async reloadShows(){
    const params = this.buildParams(
      this.selectedYear,
      this.showMonthFilter,
      this.selectedIdsClassrooms,
      this.profilesFilter,
      this.showStatusFilter,
      this.showReleasedFilter,
      this.titleText,
      this.idSchool
    );
    const response = await this.showsService.findAll(params).toPromise()
    this.shows = response
  }

  private buildParams(
    year?:string,
    month?:string,
    idsClassrooms?:string[],
    idsProfiles?:string[],
    active?:boolean,
    released?:boolean,
    title?: string,
    idSchool?: string
  ): ShowParamsDto{
    return {
      year: year,
      month: month,
      idsClassrooms: idsClassrooms,
      idsProfiles: idsProfiles,
      active: active,
      released: released,
      title: title,
      idSchool: idSchool
    }
  }

  async createShow(
    dialogRef: MatDialogRef<ShowFormComponent, Show>,
    show: CreateShowDto
  ) {
    const dialogInfo: GenericDialogDto = {
      imageUrl: '',
      primaryText: 'Você tem certeza que deseja salvar este evento?',
      secondaryText: 'Após evento ser salvo, não poderá ser feita atualização da configuração do mapa de assentos.',
      btnCancelText: 'Não, cancelar!',
      btnConfirmText: 'Sim, salvar!',
    };
    const confirmation = await genericDialog(
      this.dialog,
      dialogInfo,
      'custom-dialog-confirmation'
    ).toPromise();
    if (confirmation) {
      this.showsService
        .create(show)
        .toPromise()
        .then(
          (_) => {
            this.snackbarService.open(
              'O evento foi criado',
              SnackbarTypeEnum.SUCCESS,
              'center',
              'bottom'
            );
            dialogRef.close();
            this.reloadShows()
          },
          (error) => {
            console.log(error);
            this.snackBar.openSnackBar('Falha ao criar novo evento', 'ok');
            dialogRef.close();
          }
        );
    }
  }

  updateShow(
    dialogRef: MatDialogRef<ShowFormComponent, Show>,
    show: UpdateShowDto,
    showId: string
  ) {
    this.showsService
      .update(showId, show)
      .toPromise()
      .then(
        (_) => {
          this.snackbarService.open(
            'As alterações foram salvas',
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          dialogRef.close();
          this.reloadShows()
        },
        (error) => {
          console.log(error);
          this.snackBar.openSnackBar('Falha ao editar show', 'ok');
          dialogRef.close();
        }
      );
  }

  activateShow(
    dialogRef: MatDialogRef<GenericDialogComponent, Show>,
    showId: string
  ) {
    this.showsService
      .activate(showId)
      .toPromise()
      .then(
        (_) => {
          this.snackbarService.open(
            'O evento foi reativado',
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          dialogRef.close();
          this.reloadShows()
        },
        (error) => {
          console.log(error);
          this.snackBar.openSnackBar('Falha ao reativar o evento', 'ok');
          dialogRef.close();
        }
      );
  }

  deactivateShow(
    dialogRef: MatDialogRef<GenericDialogComponent, Show>,
    showId: string
  ) {
    this.showsService
      .deactivate(showId)
      .toPromise()
      .then(
        (_) => {
          this.snackbarService.open(
            'O evento foi cancelado',
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          dialogRef.close();
          this.reloadShows()
        },
        (error) => {
          console.log(error);
          this.snackBar.openSnackBar('Falha ao cancelar evento', 'ok');
          dialogRef.close();
        }
      );
  }
  //#endregion

  //#region form dialog
  openFormDialog(show?: Show): void {
    this.dialogConfig.data = {
      show: show
    };
    const dialogRef = this.dialog.open(ShowFormComponent, this.dialogConfig);

    this.subscribeDialog =
      dialogRef.componentInstance.onCreateShow.subscribe(
        (show: CreateShowDto) => {
          this.createShow(dialogRef, show);
        }
      );

    this.subscribeDialog =
      dialogRef.componentInstance.onUpdateShow.subscribe(
        (showUpdate: UpdateShowDto) => {
          this.updateShow(dialogRef, showUpdate, show?.id!);
        }
      );

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }

  openActiveDialog(show: Show) {

    if(show.active){
      this.dialogConfig.data = {
        imageUrl: '../../../../../assets/images/img-delete-channel.svg',
        primaryText: 'Você tem certeza que deseja desativar este evento?',
        btnCancelText: 'Não, cancelar',
        btnConfirmText: 'Sim, desativar',
      } as GenericDialogDto;
    } else {
      this.dialogConfig.data = {
        imageUrl: '../../../../../assets/images/discard-changes-news-form.svg',
        primaryText: 'Você tem certeza que deseja reativar este evento?',
        btnCancelText: 'Não, cancelar',
        btnConfirmText: 'Sim, reativar',
      } as GenericDialogDto;
    }

    const dialogRef = this.dialog.open(
      GenericDialogComponent,
      this.dialogConfig
    );
    this.subscribeDialog = dialogRef.componentInstance.onConfirm.subscribe(
      () => {
        if(show.active){
          this.deactivateShow(dialogRef, show.id)
        }
        else{
          this.activateShow(dialogRef,show.id)
        }
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }

  openReportDialog(show:Show){
    this.dialogConfig.data = {
      show: show
    };
    const dialogRef = this.dialog.open(BookingsReportComponent, this.dialogConfig);

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }
  //#endregion
}
